<template>
  <div class="p-4 sm:p-6">
    <!-- Page Header with Buttons -->
    <div class="mb-6 sm:mb-8 flex justify-between items-center">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Customer Information</h1>

      <div class="flex gap-3">
        <button
          @click="openChangePasswordModal"
          class="px-4 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-white flex items-center gap-2"
          :style="{ backgroundColor: brandColors.accent }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
            <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clip-rule="evenodd" />
          </svg>
          Change Password
        </button>
        <button
          @click="openEditModal"
          class="px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-white"
          :style="{ backgroundColor: brandColors.primary }"
        >
          Edit Profile
        </button>
      </div>
    </div>

    <!-- Profile Card -->
    <div class="bg-white dark:bg-black overflow-hidden mb-6">

      <!-- Content Section -->
      <div class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Personal Information -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <svg class="w-5 h-5 mr-2" :style="{ color: brandColors.accent }" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
              </svg>
              Personal Information
            </h3>

            <div class="space-y-2">
              <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
                <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">First Name</label>
                <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ currentUser?.first_name || 'N/A' }}</p>
              </div>

              <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
                <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">Last Name</label>
                <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ currentUser?.last_name || 'N/A' }}</p>
              </div>

              <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
                <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">Brand</label>
                <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ currentUser?.brand_name || 'N/A' }}</p>
              </div>
            </div>
          </div>

          <!-- Contact Information -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <svg class="w-5 h-5 mr-2" :style="{ color: brandColors.accent }" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              Contact Information
            </h3>

            <div class="space-y-2">
              <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
                <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">Email Address</label>
                <p class="mt-1 text-gray-900 dark:text-white font-medium break-all">{{ currentUser?.email || 'N/A' }}</p>
              </div>

              <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
                <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">Mobile Number</label>
                <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ currentUser?.mobile || 'N/A' }}</p>
              </div>

              <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
                <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">Account Status</label>
                <p class="mt-1 text-gray-900 dark:text-white font-medium capitalize">{{ currentUser?.status || 'N/A' }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Address Information -->
        <div class="mt-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
            <svg class="w-5 h-5 mr-2" :style="{ color: brandColors.accent }" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
            </svg>
            Address Information
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
              <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">House No.</label>
              <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ currentUser?.house_no || 'N/A' }}</p>
            </div>

            <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
              <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">Street</label>
              <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ currentUser?.street || 'N/A' }}</p>
            </div>

            <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
              <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">Barangay</label>
              <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ currentUser?.barangay || 'N/A' }}</p>
            </div>

            <div class="border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-black rounded-lg p-2">
              <label class="text-xs font-medium uppercase tracking-wide" :style="{ color: brandColors.primary }">City</label>
              <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ currentUser?.city || 'N/A' }}</p>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>

  <!-- Edit Profile Modal -->
  <EditProfileModal
    :is-open="showEditModal"
    :user-profile="currentUser"
    :loading="loading"
    :error="error"
    @close="closeEditModal"
    @submit="handleUpdateProfile"
  />

  <!-- Change Password Modal -->
  <ChangePasswordModal
    :is-open="showChangePasswordModal"
    :loading="loading"
    :error="error"
    @close="closeChangePasswordModal"
    @submit="handleChangePassword"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useBrand } from '@/composables/brand/useBrand'
import { customerProfileService } from '@/services/api/customerProfileService'
import type { ProfileUpdateData } from '@/services/api/customerProfileService'
import { useToast } from 'vue-toastification'
import EditProfileModal from '@/components/customer/EditProfileModal.vue'
import ChangePasswordModal from '@/components/customer/ChangePasswordModal.vue'
import api from '@/services/api'

const toast = useToast()

// Composables
const authStore = useAuthStore()
const { brandColors } = useBrand()

// Local state
const showEditModal = ref(false)
const showChangePasswordModal = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)

// Computed Properties
const currentUser = computed(() => authStore.currentUser)

// Methods
function openEditModal() {
  showEditModal.value = true
  error.value = null
}

function closeEditModal() {
  showEditModal.value = false
  error.value = null
}

async function handleUpdateProfile(data: ProfileUpdateData) {
  loading.value = true
  error.value = null

  try {
    const response = await customerProfileService.updateProfile(data)

    // Update the auth store with the new user data from the response
    if (response.data) {
      authStore.updateUserData(response.data)
    }

    toast.success('Profile updated successfully')
    closeEditModal()
  } catch (err: any) {
    // Extract user-friendly error message
    let errorMessage = 'Failed to update profile'

    if (err.response?.data?.message) {
      // Use the specific error message from backend
      errorMessage = err.response.data.message
    } else if (err.response?.data?.errors) {
      // If there are field-specific errors, get the first one
      const errors = err.response.data.errors
      const firstErrorKey = Object.keys(errors)[0]
      if (firstErrorKey && errors[firstErrorKey]) {
        errorMessage = Array.isArray(errors[firstErrorKey])
          ? errors[firstErrorKey][0]
          : errors[firstErrorKey]
      }
    }

    error.value = errorMessage
    toast.error(errorMessage)

    // Keep modal open so user can see the error and fix it
  } finally {
    loading.value = false
  }
}

function openChangePasswordModal() {
  showChangePasswordModal.value = true
  error.value = null
}

function closeChangePasswordModal() {
  showChangePasswordModal.value = false
  error.value = null
}

async function handleChangePassword(data: { currentPassword: string; newPassword: string }) {
  loading.value = true
  error.value = null

  try {
    const response = await api.put('/customer/change-password', {
      current_password: data.currentPassword,
      new_password: data.newPassword
    })

    toast.success('Password changed successfully')
    closeChangePasswordModal()
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || 'Failed to change password'
    error.value = errorMessage
    toast.error(errorMessage)
  } finally {
    loading.value = false
  }
}
</script>


